import MainContainer from "../../layout/MainContainer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import IosSwitchMain from "../../components/iosSwitchMain";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditText from "../../components/editText";
import { showError, showToast } from "../../constants/toasts";
import { useLazyGetCategoryQuery } from "../../service/category";
import { Categories, Offer } from "../../types/General";
import { OFFER_TYPE } from "../../utils/enums";
import {
  useAddUserForDiningMutation,
  useAddUserForEventMutation,
  useLazyGetOfferByIdQuery,
  useLazyGetOffersQuery,
} from "../../service/offer";
import dayjs, { Dayjs } from "dayjs";
import Loader from "../../constants/Loader";
import useAuth from "../../hooks/useAuth";
import { useLazyAllMembersQuery } from "../../service/user";
import { useLazyGetDietaryQuery } from "../../service/cms";
import { isValidEmail, isValidNumber } from "../../utils/validations";
import ReactQuill from "react-quill";

type User =
  | {
      userId: string;
      name: string;
      email: string;
      membershipType: string;
      membership: string;
      createdAt: string;
      paymentStatus: string;
      phoneNumber: string;
    }
  | any;

type InputType = "Checkbox" | "Radio" | "Text" | "TextArea" | "Date";

interface Option {
  id: number;
  optionText: string;
}

interface Question {
  id: number;
  label: string;
  inputType: InputType;
  options: Option[];
}

interface Answer {
  questionId: number;
  textValue: string;
  selectedOptionId: number;
  selectedOptionIds: number[];
  numberValue: number;
}

type SelectedUser = {
  name: string;
  email: string;
  roles: string;
};

function AddUserBooking() {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const userData = useAuth();
  const [getCategory] = useLazyGetCategoryQuery();
  const [getOffers] = useLazyGetOffersQuery();
  const [getOfferDetail] = useLazyGetOfferByIdQuery();
  const [addUser] = useAddUserForDiningMutation();
  const [getUsers] = useLazyAllMembersQuery();
  const [getDietary] = useLazyGetDietaryQuery();
  const [addUserForEvent] = useAddUserForEventMutation();

  const [isPartnerIncluded, setIsPartnerIncluded] = useState(false);
  const [isGuestIncluded, setIsGuestIncluded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [categoryData, setCategoryData] = useState<Categories[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [offerData, setOfferData] = useState<Offer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState("");
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [date, setDate] = useState<Dayjs | null>(null);
  const [time, setTime] = useState<Dayjs | null>(null);
  const [guests, setGuests] = useState("");
  const [offerDetail, setOfferDetails] = useState<Offer | null>(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [dietary, setDietary] = useState([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedType, setSelectedType] = useState("");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [selectedPartnerPlan, setSelectedPartnerPlan] = useState<string[]>([]);
  const [partnerPayer, setPartnerPayer] = useState("");
  const [specialRequest, setSpecialRequest] = useState("");
  const [paymentForUser, setPaymentForUser] = useState("");
  const [notes, setNotes] = useState("");

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }], // <-- Text & background color
      ["link", "image"],
      ["clean"], // remove formatting
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "link",
    "image",
  ];

  const handleSelectType = (event: SelectChangeEvent) => {
    setError(false);
    const { value } = event.target;
    setSelectedType(value);
  };

  const handleChangeCategory = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setSelectedCategory(value);
    const selected = categoryData?.find((item) => item?.id === value);
  };

  const handleChangePlan = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setSelectedPlan(value);
  };

  const handlePartnerPayer = (event: SelectChangeEvent) => {
    const { value } = event.target;
    setPartnerPayer(value);
  };

  const handleChangePartnerPlan = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value as string[];
    setSelectedPartnerPlan(value);
  };

  const [guest, setGuest] = useState({
    name: "",
    email: "",
    payer: "",
    guestDietaries: [] as string[], // now an array
  });

  const handleDelete = (index: number) => {
    setGuestData((prev: any) =>
      prev?.filter((_: any, i: number) => i !== index)
    );
  };

  const [guestData, setGuestData] = useState<any[]>([]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;

    if (name === "guestDietaries") {
      setGuest((prev) => ({
        ...prev,
        [name]: typeof value === "string" ? value.split(",") : value,
      }));
    } else {
      setGuest((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const updateAnswer = (questionId: number, update: Partial<Answer>) => {
    setAnswers((prev) => {
      const existing = prev.find((a) => a.questionId === questionId);
      if (existing) {
        return prev.map((a) =>
          a.questionId === questionId ? { ...a, ...update } : a
        );
      } else {
        return [
          ...prev,
          {
            questionId,
            textValue: "",
            selectedOptionId: 0,
            selectedOptionIds: [],
            numberValue: 0,
            ...update,
          },
        ];
      }
    });
  };

  const handleAddGuest = () => {
    if (!guest.name || !guest.email || !guest.payer) {
      showError("Please fill all required fields");
      return;
    }

    const newGuest = {
      name: guest.name,
      email: guest.email,
      payer: guest.payer,
      guestDietaries: guest?.guestDietaries.map((d) => ({ name: d })),
    };

    setGuestData((prev) => [...prev, newGuest]);

    // reset form
    setGuest({
      name: "",
      email: "",
      payer: "",
      guestDietaries: [],
    });
  };

  const renderField = (q: Question) => {
    const current = answers.find((a) => a.questionId === q.id);

    switch (q.inputType) {
      case "Checkbox":
        return (
          <div className="control_group w_100 prfrnc">
            <FormGroup>
              {q.options.map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  control={
                    <Checkbox
                      checked={
                        current?.selectedOptionIds?.includes(opt.id) || false
                      }
                      onChange={(e) => {
                        const updated = current?.selectedOptionIds || [];
                        updateAnswer(q.id, {
                          selectedOptionIds: e.target.checked
                            ? [...updated, opt.id]
                            : updated.filter((id) => id !== opt.id),
                        });
                      }}
                    />
                  }
                  label={opt.optionText}
                />
              ))}
            </FormGroup>
          </div>
        );

      case "Radio":
        return (
          <div className="control_group w_100 prfrnc">
            <RadioGroup
              value={current?.selectedOptionId || ""}
              onChange={(e) =>
                updateAnswer(q.id, { selectedOptionId: Number(e.target.value) })
              }
            >
              {q.options.map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  value={opt.id}
                  control={<Radio />}
                  label={opt.optionText}
                />
              ))}
            </RadioGroup>
          </div>
        );

      case "Text":
        return (
          <div className="control_group w_33">
            <TextField
              variant="outlined"
              fullWidth
              placeholder={q.label}
              hiddenLabel
              value={current?.textValue || ""}
              onChange={(e) =>
                updateAnswer(q.id, { textValue: e.target.value })
              }
            />
          </div>
        );

      case "TextArea":
        return (
          <div className="control_group w_33">
            <TextField
              fullWidth
              multiline
              hiddenLabel
              rows={4}
              placeholder={q.label}
              value={current?.textValue || ""}
              onChange={(e) =>
                updateAnswer(q.id, { textValue: e.target.value })
              }
            />
          </div>
        );

      case "Date":
        return (
          // <TextField
          //   type="date"
          //   fullWidth
          //   size="small"
          //   value={current?.textValue || dayjs().format("YYYY-MM-DD")}
          //   onChange={(e) => updateAnswer(q.id, { textValue: e.target.value })}
          // />

          <div className="w_33 control_group date_field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                className="form_control"
                value={current?.textValue ? dayjs(current.textValue) : null}
                // Convert picked Dayjs -> string before saving
                onChange={(newValue: Dayjs | null) =>
                  updateAnswer(q.id, {
                    textValue: newValue ? newValue.toISOString() : "",
                  })
                }
                minDate={dayjs()}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    placeholder: "End Date",
                    sx: {
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "#9999", // change text & placeholder color
                        opacity: 1,
                      },
                    },
                  },
                }}
                slots={{
                  openPickerIcon: () => (
                    <img src="/static/images/calendar_icon.svg" alt="icon" />
                  ),
                  // textField: (params) => (
                  //   <TextField
                  //     {...params}
                  //     fullWidth
                  //     placeholder="End Date"
                  //     hiddenLabel
                  //   />
                  // ),
                }}
                desktopModeMediaQuery="(min-width:0px)"
              />
            </LocalizationProvider>
          </div>
        );

      default:
        return null;
    }
  };

  const validateQuestions = () => {
    let valid = true;
    const newErrors: Record<number, string> = {}; // { questionId: "Error message" }

    questions.forEach((q: any) => {
      const ans = answers.find((a) => a.questionId === q.id);

      switch (q.inputType) {
        case "Checkbox":
          if (!ans?.selectedOptionIds || ans.selectedOptionIds.length === 0) {
            newErrors[q.id] = "This question is required";
            valid = false;
          }
          break;
        case "Radio":
          if (!ans?.selectedOptionId) {
            newErrors[q.id] = "This question is required";
            valid = false;
          }
          break;
        case "Text":
        case "TextArea":
          if (!ans?.textValue?.trim()) {
            newErrors[q.id] = "This question is required";
            valid = false;
          }
          break;
        case "Date":
          if (!ans?.textValue) {
            newErrors[q.id] = "This question is required";
            valid = false;
          }
          break;
      }
    });

    return valid;
  };

  const getUsersApi = async () => {
    setLoading(true);
    try {
      const response = await getUsers({
        search: "",
      }).unwrap();

      setLoading(false);

      if (response.status === 200) {
        setUsers(response?.data || []);
      } else {
        setUsers([]);
      }
    } catch (error: any) {
      setLoading(false);
    }
  };

  const addUserApi = async () => {
    setError(true);

    if (
      !selectedEmail ||
      !name ||
      !selectedPlan ||
      !guests ||
      !date ||
      !time ||
      !selectedCategory ||
      !selectedOffer
    ) {
      return;
    }

    // Validate dynamic questions
    if (!validateQuestions()) {
      showError("Please answer all required questions");
      return;
    }

    const body = {
      bookingType: "Dining",
      offerId: selectedOffer,
      bookingByUserId: userData?.id,
      email: selectedEmail,
      name: name,
      numberOfGuest: guests,
      bookingDate: time,
      bookingTime: dayjs(time).format("HH:mm:ss"),
      isCancellationPolicy: offerDetail?.isCancellationPolicy,
      dietaryRequirements: selectedPlan?.map((name) => ({ name })),
      answers: answers,
    };

    try {
      const response = await addUser(body).unwrap();
      if (response.status === 200) {
        showToast(response?.message || "");
        goBack();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const addUserForEventApi = async () => {
    setError(true);

    if (
      !selectedEmail ||
      !name ||
      !selectedPlan ||
      !selectedCategory ||
      !selectedOffer
    ) {
      return;
    }

    if (
      isPartnerIncluded &&
      (!partnerEmail ||
        !partnerName ||
        !selectedPartnerPlan ||
        !partnerPayer ||
        !specialRequest)
    ) {
      return;
    }

    if (isGuestIncluded && !guestData?.length) {
      showError("Please add guests");
      return;
    }

    // Validate dynamic questions
    if (!validateQuestions()) {
      showError("Please answer all required questions");
      return;
    }

    const body = {
      bookingType: "CreateBooking",
      offerId: selectedOffer,
      bookingCreatedBy: userData?.id,
      name: name,
      email: selectedEmail,
      isPartnerAdded: isPartnerIncluded,
      isGuestAdded: isGuestIncluded,
      partnerDetails: {
        name: partnerName,
        email: partnerEmail,
        payer: partnerPayer,
        specialRequest: specialRequest,
        partnerDietary: selectedPartnerPlan?.map((name) => ({ name })),
      },
      userDietaryRequests: selectedPlan?.map((name) => ({ name })),
      offerGuestInviteds: guestData,
      answers: answers,
    };

    try {
      const response = await addUserForEvent(body).unwrap();
      if (response.status === 200) {
        showToast(response?.message || "");
        goBack();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const addUserForDealerApi = async () => {
    setError(true);

    if (
      !selectedEmail ||
      !name ||
      !selectedPlan ||
      !specialRequest ||
      !paymentForUser
    ) {
      return;
    }

    // Validate dynamic questions
    if (!validateQuestions()) {
      showError("Please answer all required questions");
      return;
    }

    const body = {
      bookingType: "ContactDealer",
      offerId: selectedOffer,
      bookingByUserId: userData?.id,
      email: selectedEmail,
      name: name,
      contactDealerNotes: notes,
      contactDealerAmount: paymentForUser,
      dietaryRequirements: selectedPlan?.map((name) => ({ name })),
      answers: answers,
    };

    try {
      const response = await addUser(body).unwrap();
      if (response.status === 200) {
        showToast(response?.message || "");
        goBack();
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getCategoryApi = async () => {
    try {
      const response = await getCategory({ search: "" }).unwrap();
      if (response.status === 200) {
        setCategoryData(response?.data || []);
        // setCount(response?.data?.totalCount);
      } else {
        setCategoryData([]);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const getOffersApi = async () => {
    try {
      setLoading(true);
      const response = await getOffers({
        page: 1,
        search: "",
        categoryId: selectedCategory,
        status: "active",
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setOfferData(response?.data?.items || []);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getOfferDetailApi = async () => {
    try {
      setLoading(true);
      const response = await getOfferDetail({
        offerId: selectedOffer,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setOfferDetails(response?.data);
        setQuestions(response?.data?.questions || []);
      } else {
        setLoading(false);
        setOfferDetails(null);
        setQuestions([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getDietaryApi: any = async () => {
    setLoading(true);
    try {
      const response = await getDietary({
        page: 1,
        search: "",
      }).unwrap();
      if (response?.status === 200) {
        setLoading(false);
        setDietary(response?.data || []);
      } else {
        setLoading(false);
        setDietary([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getCategoryApi();
    getUsersApi();
    getDietaryApi();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      getOffersApi();
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedOffer) {
      getOfferDetailApi();
    }
  }, [selectedOffer]);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title d_flex">
        <div className="left_s">
          <h1 className="back_arrow">
            <IconButton onClick={goBack}>
              <ArrowBackIcon />
            </IconButton>
            Add User
          </h1>
        </div>
      </div>

      <div className="cards add_user_link">
        <div className="gap_p">
          {/* Category Dropdown */}
          <div className="control_group w_33">
            <Select
              className="dropse"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              inputProps={{
                "aria-label": "Without label",
              }}
              displayEmpty
              value={selectedType}
              onChange={handleSelectType}
              fullWidth
            >
              <MenuItem value="" disabled>
                Select Type
              </MenuItem>
              <MenuItem value="Restaurant">Dining</MenuItem>
              <MenuItem value="Events">Event</MenuItem>
              <MenuItem value="Contact Dealers">Contact Dealer</MenuItem>
            </Select>
            {error && !selectedType ? (
              <p className="err_msg">Type is required</p>
            ) : (
              ""
            )}
          </div>

          {/* Dining Fields */}
          {selectedType === OFFER_TYPE.RESTAURANT ? (
            <>
              <div className="control_group w_33">
                <Select
                  className="dropse"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  value={selectedCategory}
                  name="page"
                  onChange={handleChangeCategory}
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>

                  {categoryData?.map((item) => {
                    return (
                      <MenuItem value={item?.id} key={item?.id}>
                        {item?.name || "-"}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && !selectedCategory ? (
                  <p className="err_msg">Category is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={offerData}
                  disabled={!selectedCategory}
                  getOptionLabel={(option) => option?.title} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedOffer(value?.id);
                    } else {
                      setSelectedOffer("");
                    }
                  }}
                  sx={{
                    width: 300,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#999", // text color when disabled
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select offers"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedOffer ? (
                  <p className="err_msg">Offer is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={users}
                  getOptionLabel={(option) => option?.email} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedEmail(value?.email);
                    } else {
                      setSelectedEmail(null);
                    }
                  }}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Email Address"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedEmail ? (
                  <p className="err_msg">Email is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="Name"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 40 }}
                  value={name}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setName(e.target.value);
                    }
                  }}
                />
                {error && !name ? (
                  <p className="err_msg">Name is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="form_group w_33">
                <Select
                  multiple
                  value={selectedPlan}
                  name="page"
                  onChange={handleChangePlan}
                  displayEmpty
                  renderValue={(selected: any) =>
                    selected?.length === 0
                      ? "Select Dietary Requirements"
                      : selected?.join(", ")
                  }
                >
                  <MenuItem value="" disabled>
                    Select Dietary Requirements
                  </MenuItem>
                  {dietary?.map((item: any, index: number) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {error && !selectedPlan?.length ? (
                  <p className="err_msg">Dietary Requirements is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="No. of Guests"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 40 }}
                  // InputProps={{ readOnly: true }}
                  value={guests}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setGuests(e.target.value);
                    }
                  }}
                />
                {error && !guests ? (
                  <p className="err_msg">Guests is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="w_33 control_group date_field">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="form_control"
                    value={date}
                    onChange={(newValue) => setDate(newValue)}
                    minDate={dayjs(offerDetail?.offerStartDate)}
                    maxDate={dayjs(offerDetail?.offerEndDate)}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "End Date",
                        sx: {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#9999", // change text & placeholder color
                            opacity: 1,
                          },
                        },
                      },
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/calendar_icon.svg"
                          alt="icon"
                        />
                      ),
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                  />
                  {error && !date ? (
                    <p className="err_msg">End Date is required</p>
                  ) : (
                    ""
                  )}
                </LocalizationProvider>
              </div>
              <div className="w_33 control_group date_field">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    value={time}
                    onChange={(newValue) => {
                      if (date) {
                        setTime(
                          dayjs(date)
                            .hour(dayjs(newValue).hour())
                            .minute(dayjs(newValue).minute())
                        );
                      }
                    }}
                    slotProps={{
                      textField: {
                        placeholder: "Start Time",
                        sx: {
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "#9999", // change text & placeholder color
                            opacity: 1,
                          },
                        },
                      },
                    }}
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/calendar_icon.svg"
                          alt="icon"
                        />
                      ),
                    }}
                    disabled={!date}
                  />
                </LocalizationProvider>
                {error && !time ? (
                  <p className="err_msg">Start Time is required</p>
                ) : (
                  ""
                )}
              </div>
              {questions?.map((q: any) => (
                <div key={q?.id} style={{ marginBottom: 20 }}>
                  <label>{q?.label}</label>

                  {renderField(q)}
                </div>
              ))}
            </>
          ) : null}
          {/* Event Fields */}
          {selectedType === OFFER_TYPE.EVENTS ? (
            <>
              <div className="control_group w_33">
                <Select
                  className="dropse"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  value={selectedCategory}
                  name="page"
                  onChange={handleChangeCategory}
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>

                  {categoryData?.map((item) => {
                    return (
                      <MenuItem value={item?.id} key={item?.id}>
                        {item?.name || "-"}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && !selectedCategory ? (
                  <p className="err_msg">Category is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={offerData}
                  disabled={!selectedCategory}
                  getOptionLabel={(option) => option?.title} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedOffer(value?.id);
                    } else {
                      setSelectedOffer("");
                    }
                  }}
                  sx={{
                    width: 300,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#999", // text color when disabled
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select offers"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedOffer ? (
                  <p className="err_msg">Offer is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={users}
                  getOptionLabel={(option) => option?.email} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedEmail(value?.email);
                    } else {
                      setSelectedEmail(null);
                    }
                  }}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Email Address"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedEmail ? (
                  <p className="err_msg">Email is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="Name"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 40 }}
                  value={name}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setName(e.target.value);
                    }
                  }}
                />
                {error && !name ? (
                  <p className="err_msg">Name is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="form_group w_33">
                <Select
                  multiple
                  value={selectedPlan}
                  name="page"
                  onChange={handleChangePlan}
                  displayEmpty
                  renderValue={(selected: any) =>
                    selected?.length === 0
                      ? "Select Dietary Requirements"
                      : selected?.join(", ")
                  }
                >
                  <MenuItem value="" disabled>
                    Select Dietary Requirements
                  </MenuItem>
                  {dietary?.map((item: any, index: number) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {error && !selectedPlan?.length ? (
                  <p className="err_msg">Dietary Requirements is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group cncl_plcy w_50">
                <FormLabel sx={{ color: "white", fontSize: 16 }}>
                  Include Partner
                </FormLabel>

                <IosSwitchMain
                  checked={isPartnerIncluded}
                  onChange={(e) => setIsPartnerIncluded(e.target.checked)}
                />
              </div>
              {isPartnerIncluded && (
                <>
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <TextField
                        variant="outlined"
                        placeholder="Email"
                        fullWidth
                        hiddenLabel
                        inputProps={{ maxLength: 40 }}
                        value={partnerEmail}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\s/g, "");
                          setPartnerEmail(value);
                        }}
                      />
                      {error && !partnerEmail ? (
                        <p className="err_msg">Email is required</p>
                      ) : error && !isValidEmail(partnerEmail) ? (
                        <p className="err_msg">Please enter a valid email</p>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="control_group w_33">
                      <TextField
                        variant="outlined"
                        placeholder="Name"
                        fullWidth
                        hiddenLabel
                        inputProps={{ maxLength: 40 }}
                        value={partnerName}
                        onChange={(e) => {
                          if (
                            e.target.value === " " ||
                            e.target.value === "."
                          ) {
                          } else {
                            setPartnerName(e.target.value);
                          }
                        }}
                      />
                      {error && !partnerName ? (
                        <p className="err_msg">Name is required</p>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="form_group w_33">
                      <Select
                        multiple
                        value={selectedPartnerPlan}
                        name="page"
                        onChange={handleChangePartnerPlan}
                        displayEmpty
                        renderValue={(selected: any) =>
                          selected?.length === 0
                            ? "Select Dietary Requirements"
                            : selected?.join(", ")
                        }
                      >
                        <MenuItem value="" disabled>
                          Select Dietary Requirements
                        </MenuItem>
                        {dietary?.map((item: any, index: number) => (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                      {error && !selectedPartnerPlan?.length ? (
                        <p className="err_msg">
                          Dietary Requirements is required
                        </p>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="control_group w_33">
                      <Select
                        className="dropse"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        inputProps={{
                          "aria-label": "Without label",
                        }}
                        displayEmpty
                        name="type"
                        value={partnerPayer}
                        onChange={handlePartnerPayer}
                      >
                        <MenuItem value="" disabled>
                          Select Payer
                        </MenuItem>
                        <MenuItem value="Primary">Primary</MenuItem>
                        <MenuItem value="Secondary">Secondary</MenuItem>
                      </Select>
                      {error && !partnerPayer ? (
                        <p className="err_msg">Payer is required</p>
                      ) : (
                        ""
                      )}
                    </div>
                    <div className="control_group w_33">
                      <TextField
                        variant="outlined"
                        placeholder="Any special request or occasion?"
                        fullWidth
                        hiddenLabel
                        inputProps={{ maxLength: 150 }}
                        value={specialRequest}
                        onChange={(e) => {
                          if (
                            e.target.value === " " ||
                            e.target.value === "."
                          ) {
                          } else {
                            setSpecialRequest(e.target.value);
                          }
                        }}
                      />
                      {error && !specialRequest ? (
                        <p className="err_msg">Special request is required</p>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="control_group cncl_plcy w_50">
                <FormLabel sx={{ color: "white", fontSize: 16 }}>
                  Include Guest
                </FormLabel>

                <IosSwitchMain
                  checked={isGuestIncluded}
                  onChange={(e) => setIsGuestIncluded(e.target.checked)}
                  disabled={!offerDetail?.isGuestAdded}
                />
              </div>
              {isGuestIncluded && (
                <>
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <TextField
                        variant="outlined"
                        placeholder="Email"
                        fullWidth
                        hiddenLabel
                        name="email"
                        value={guest.email}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="control_group w_33">
                      <TextField
                        variant="outlined"
                        placeholder="Name"
                        fullWidth
                        hiddenLabel
                        name="name"
                        value={guest.name}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form_group w_33">
                      <Select
                        multiple
                        value={guest.guestDietaries}
                        name="guestDietaries"
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(selected: any) =>
                          selected?.length === 0
                            ? "Select Dietary Requirements"
                            : selected?.join(", ")
                        }
                      >
                        <MenuItem value="" disabled>
                          Select Dietary Requirements
                        </MenuItem>
                        {dietary?.map((item: any, index: number) => (
                          <MenuItem key={index} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                    <div className="control_group w_50">
                      <Select
                        className="dropse"
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        inputProps={{
                          "aria-label": "Without label",
                        }}
                        displayEmpty
                        name="payer"
                        value={guest.payer}
                        onChange={handleChange}
                      >
                        <MenuItem value="" disabled>
                          Select Payer
                        </MenuItem>
                        <MenuItem value="Primary">Primary</MenuItem>
                        <MenuItem value="Secondary">Secondary</MenuItem>
                      </Select>
                    </div>

                    {guestData?.length < Number(offerDetail?.numberOfGuest) ? (
                      <div
                        className="control_group w_50"
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Button
                          className="btn btn_primary"
                          onClick={handleAddGuest}
                        >
                          Save Guest
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </>
              )}

              <div className="prvs_qstn_mn">
                <div className="d_flx1">
                  {guestData?.map((g, index) => (
                    <div
                      className="question_card"
                      style={{
                        border: "1px solid #BC8F4B",
                        borderRadius: "8px",
                        padding: 10,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between">
                        <div>
                          <Typography variant="body2">
                            Name: {g.name}
                          </Typography>
                          <Typography variant="body2">
                            Email: {g.email}
                          </Typography>
                          <Typography variant="body2">
                            Payer: {g.payer}
                          </Typography>
                          {g.guestDietaries.length > 0 && (
                            <Typography variant="body2">
                              Dietary: {g.guestDietaries[0].name}
                            </Typography>
                          )}
                        </div>

                        <IconButton
                          color="error"
                          sx={{ alignSelf: "flex-start" }}
                          onClick={() => handleDelete(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </div>
                  ))}
                </div>
              </div>

              {questions?.map((q: any) => (
                <div key={q?.id} style={{ marginBottom: 20 }}>
                  <label>{q?.label}</label>

                  {renderField(q)}
                </div>
              ))}
            </>
          ) : null}
          {selectedType === OFFER_TYPE.CONTACT_DEALERS ? (
            <>
              <div className="control_group w_33">
                <Select
                  className="dropse"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  value={selectedCategory}
                  name="page"
                  onChange={handleChangeCategory}
                >
                  <MenuItem value="" disabled>
                    Select Category
                  </MenuItem>

                  {categoryData?.map((item) => {
                    return (
                      <MenuItem value={item?.id} key={item?.id}>
                        {item?.name || "-"}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && !selectedCategory ? (
                  <p className="err_msg">Category is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={offerData}
                  disabled={!selectedCategory}
                  getOptionLabel={(option) => option?.title} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedOffer(value?.id);
                    } else {
                      setSelectedOffer("");
                    }
                  }}
                  sx={{
                    width: 300,
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#999", // text color when disabled
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select offers"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedOffer ? (
                  <p className="err_msg">Offer is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <Autocomplete
                  disablePortal
                  options={users}
                  getOptionLabel={(option) => option?.email} // show email in dropdown
                  onChange={(event, value) => {
                    if (value) {
                      setSelectedEmail(value?.email);
                    } else {
                      setSelectedEmail(null);
                    }
                  }}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Email Address"
                      hiddenLabel
                    />
                  )}
                />
                {error && !selectedEmail ? (
                  <p className="err_msg">Email is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="Name"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 40 }}
                  value={name}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setName(e.target.value);
                    }
                  }}
                />
                {error && !name ? (
                  <p className="err_msg">Name is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="form_group w_33">
                <Select
                  multiple
                  value={selectedPlan}
                  name="page"
                  onChange={handleChangePlan}
                  displayEmpty
                  renderValue={(selected: any) =>
                    selected?.length === 0
                      ? "Select Dietary Requirements"
                      : selected?.join(", ")
                  }
                >
                  <MenuItem value="" disabled>
                    Select Dietary Requirements
                  </MenuItem>
                  {dietary?.map((item: any, index: number) => (
                    <MenuItem key={index} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                {error && !selectedPlan?.length ? (
                  <p className="err_msg">Dietary Requirements is required</p>
                ) : (
                  ""
                )}
              </div>
              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="Any special request or occasion?"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 150 }}
                  value={specialRequest}
                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setSpecialRequest(e.target.value);
                    }
                  }}
                />
                {error && !specialRequest ? (
                  <p className="err_msg">Special request is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_33">
                <TextField
                  variant="outlined"
                  placeholder="Payment for user(amount)"
                  fullWidth
                  hiddenLabel
                  inputProps={{ maxLength: 10 }}
                  value={paymentForUser}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (isValidNumber(value)) {
                      setPaymentForUser(value);
                    }
                  }}
                />
                {error && !paymentForUser ? (
                  <p className="err_msg">amount is required</p>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_50">
                <FormLabel className="form_label">
                  Additional Information
                </FormLabel>
                <ReactQuill
                  value={notes}
                  onChange={setNotes}
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  style={{ height: "200px", marginBottom: "50px" }}
                />
              </div>

              {questions?.map((q: any) => (
                <div key={q?.id} style={{ marginBottom: 20 }}>
                  <label>{q?.label}</label>

                  {renderField(q)}
                </div>
              ))}
            </>
          ) : null}
          {/* Save Button */}
          <div className="mt_30">
            <Button
              className="btn btn_primary"
              onClick={() => {
                if (selectedType === OFFER_TYPE.RESTAURANT) {
                  addUserApi();
                } else if (selectedType === OFFER_TYPE.EVENTS) {
                  addUserForEventApi();
                } else if (selectedType === OFFER_TYPE.CONTACT_DEALERS) {
                  addUserForDealerApi();
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
}

export default AddUserBooking;
